// @ts-strict-ignore
import {createRoot} from 'react-dom/client';
import {PureComponent} from 'react';
import debounce from 'lodash/debounce';
import uniqueBy from 'lodash/uniqBy';
import produce from 'immer';
import moment from 'moment';
import React from 'react';

import {promisifyModal, REACT_MODAL_TAG_NAME} from 'common/components/modal/utils';
import {isValidEmailAddress} from 'common/util/string';
import {KEY_VALUES} from 'common/util/dom';
import {Section} from 'common/components/items-menu/types';
import {ProjectMember as ProjectMemberFull, ProjectMemberResponse} from 'common/types/app-api/project-member';
import {apiClient} from 'common/util/api-client';
import {reporter} from 'common/report/error-reporting';
import {ExportDashboardModalPage} from 'common/components/modal/types';
import {
  DashboardDigest,
  DashboardDigestSlackSubscription,
  DIGEST_DATE_FORMAT,
  DigestRecurrence,
  SerializedDashboardDigest,
} from 'common/query-builder/query-report-header/subscribe-to-boards-menu/types';
import {ToastType} from 'common/components/toast/types';
import {addToast} from 'common/components/toast-area/toast-manager';
import {SelectedChannelsChangedDetail} from 'common/widgets/slack-channel-selector/types';
import {DomainGetResponse} from 'common/report/queries/app-api/legacy/org-types';
import {fetchDomainData} from 'common/report/queries/app-api/legacy/orgs-settings-client';
import {CurrentProjectResource} from 'common/report/model-resources/projects';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {LocalizationStore} from 'common/i18n/store';
import {useTranslation} from 'common/i18n/hooks';

import {DIGEST_COPY} from '../subscribe-to-boards-menu/constants';
import {DigestTrackingEvent, track} from '../subscribe-to-boards-menu/tracking';
import {getDigestTrackingData} from '../subscribe-to-boards-menu/util';
import {template} from './template';
import {ExportDashboardType, LoadingState, LoadingStatus} from './types';

type ProjectMember = Pick<ProjectMemberFull, `id` | `name` | `email` | `role`>;

type ExportSerializedDashboardDigest = Omit<SerializedDashboardDigest, `monthly_week_ordinal`>;

type ExportDashboardModalState = {
  availableMembers: LoadingState<ProjectMember[]>;
  currentPage: ExportDashboardModalPage;
  emailDomains: DomainGetResponse[];
  emailSubscribers: Array<Partial<ProjectMember>>;
  isLoading: boolean;
  isCreatingDigest: boolean;
  membersMap: Map<string, ProjectMember>;
  slackSubscriptions: DashboardDigestSlackSubscription[];
  subscriberSearch: string;
  subscriberSearchFocused: boolean;
};

export type ExportDashboardModalProps = {
  createDigestCall?: (
    serializedDigest: ExportSerializedDashboardDigest,
    projectId: number,
    workspaceId?: number,
    digestId?: string,
  ) => Promise<DashboardDigest>;
  currentProject: CurrentProjectResource;
  i18n: LocalizationStore;
  dashboardId: number;
  emailOrSlack: ExportDashboardType;
  onClose?: () => void;
  projectId: number;
  workspaceId?: number;
};

const getStandardAppApiUrl = (projectId: number, workspaceId?: number) => {
  const parentEntityPath = workspaceId ? `workspaces/${workspaceId}` : `projects/${projectId}`;
  return `/api/app/${parentEntityPath}`;
};

export const getDigestApiUrl = (projectId: number, workspaceId?: number, digestId = ``): string => {
  return `${getStandardAppApiUrl(projectId, workspaceId)}/dashboard-reports/email-digests/${digestId}`;
};

const defaultCreateDigestCall = (
  serializedDigest: ExportSerializedDashboardDigest,
  projectId: number,
  workspaceId?: number,
  digestId = ``,
) => {
  return apiClient.post<DashboardDigest>(getDigestApiUrl(projectId, workspaceId, digestId), serializedDigest);
};

export class ExportDashboardModal extends PureComponent<ExportDashboardModalProps, ExportDashboardModalState> {
  state = {
    availableMembers: {
      loading: LoadingStatus.Idle,
      data: null,
      error: null,
    },
    currentPage: ExportDashboardModalPage.Main,
    emailDomains: [],
    emailSubscribers: [],
    isCreatingDigest: false,
    isLoading: false,
    membersMap: new Map(),
    searchInputElement: null,
    slackSubscriptions: [],
    subscriberSearch: ``,
    subscriberSearchFocused: false,
  };

  componentDidMount(): void {
    this.updateSubscriberSearchFocused = debounce(
      (focused) => this.updateSubscriberSearchFocusedInternal(focused),
      300,
    );
    this.getUsers();
    const organizationId = this.props.currentProject.get().value?.organizationId;
    fetchDomainData(organizationId).then((res) => this.setState({emailDomains: res}));
  }

  private updateSubscriberSearchFocusedInternal = (focused: boolean) => {
    this.setState({
      subscriberSearchFocused: focused,
    });
  };

  /**
   * Debounced version of the subscriber search focused update function.
   *
   * This is required because when we select an item from the items menu there
   * is a delay between the item being selected and the input being blurred. We
   * don't want to prematurely show the error message when the user is still
   * selecting an item.
   */
  private declare updateSubscriberSearchFocused: (focused: boolean) => void;

  get isValidExport(): boolean {
    return this.state.emailSubscribers.length > 0 || this.state.slackSubscriptions.length > 0;
  }

  get updateSubscriberSearchError(): string {
    const subscriberSearch = this.state.subscriberSearch;
    if (!subscriberSearch.length) {
      return ``;
    }
    if (!isValidEmailAddress(subscriberSearch) && !this.state.subscriberSearchFocused) {
      return `Please enter a valid email address.`;
    }
    return ``;
  }

  private addSubscriberUpdate(subscriber: Partial<ProjectMember>): Pick<ExportDashboardModalState, `emailSubscribers`> {
    // Push new ones to the front of the array because the full list might
    // be cut off and we want the user to see their changes as they're made.
    return {emailSubscribers: [subscriber, ...this.state.emailSubscribers]};
  }

  private close() {
    if (this.props.onClose) {
      this.props.onClose();
    }
  }

  private createDigestCall(serializedDigest: ExportSerializedDashboardDigest, digestId = ``) {
    const func = this.props.createDigestCall || defaultCreateDigestCall;
    return func(serializedDigest, this.props.projectId, this.props.workspaceId, digestId);
  }

  private exportBoardApiCall() {
    this.setState({
      isCreatingDigest: true,
    });
    this.createDigestCall(this.getSerializedDigestFromCurrentState())
      .then((res) => {
        track(DigestTrackingEvent.ExportBoardModalSubmitted, {
          'export type': this.props.emailOrSlack,
          ...getDigestTrackingData(res, this.state.membersMap, this.props.i18n),
        });
        addToast({
          message: DIGEST_COPY.createDigestSuccess,
          type: ToastType.SUCCESS,
        });
        this.close();
      })
      .catch((error) => {
        addToast({
          message: DIGEST_COPY.createDigestError,
          type: ToastType.ERROR,
        });
        reporter.error(error);
      })
      .finally(() => {
        this.setState({
          isCreatingDigest: false,
        });
      });
  }

  private getSerializedDigestFromCurrentState(): ExportSerializedDashboardDigest {
    return {
      recur: DigestRecurrence.ASAP,
      start_date: moment().format(DIGEST_DATE_FORMAT),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      slack_subscriptions: this.state.slackSubscriptions,
      recipients: this.state.emailSubscribers.map((sub) => sub.email),
      dashboard_id: this.props.dashboardId,
    };
  }

  private isExternalUser(member: Partial<ProjectMember>): boolean {
    return !member.id;
  }

  getExternalUsers(): Array<Partial<ProjectMember>> {
    return this.state.emailSubscribers.filter((m) => this.isExternalUser(m));
  }

  getSections(): Section {
    const availableMembers = this.state.availableMembers;
    if (availableMembers.loading !== LoadingStatus.Success) {
      return {isLoading: true, items: []};
    }

    const subscribersSet = new Set<string>(this.state.emailSubscribers.map((x) => x.email));

    return {
      isLoading: false,
      items: availableMembers.data
        .filter((x) => !subscribersSet.has(x.email))
        .map((member) => ({
          label: `${member.name} <${member.email}>`,
          value: member,
        })),
    };
  }

  async getUsers() {
    // We'll just fetch users once
    if (this.state.availableMembers.loading !== LoadingStatus.Idle) {
      return;
    }
    try {
      const response = await apiClient.get<ProjectMemberResponse>(
        `/api/app/projects/${this.props.projectId}/granted-users`,
      );
      this.setState(
        produce((draft: ExportDashboardModalState) => {
          draft.availableMembers.loading = LoadingStatus.Success;
          const members = uniqueBy<ProjectMember>(
            response.members.map(
              (member): ProjectMember => ({
                id: member.id,
                name: member.name,
                email: member.email,
                role: member.role,
              }),
            ),
            `id`,
          );

          draft.availableMembers.data = members;
          draft.membersMap = new Map(members.map((x) => [x.email, x]));
        }),
      );
    } catch (err) {
      if (err.status === 403) {
        this.setState(
          produce((draft: ExportDashboardModalState) => {
            draft.availableMembers.loading = LoadingStatus.Success;
            draft.availableMembers.data = [];
          }),
        );

        return;
      }

      reporter.error(err, `Failed to fetch existing project members`);
      this.setState(
        produce((draft: ExportDashboardModalState) => {
          draft.availableMembers.loading = LoadingStatus.Failed;
          draft.availableMembers.error = err;
        }),
      );
    }
  }

  onBlur() {
    this.updateSubscriberSearchFocused(false);
  }

  onFocus() {
    this.updateSubscriberSearchFocused(true);
  }

  onCancel() {
    this.close();
  }

  onClickMember(member: ProjectMember) {
    this.setState({...this.addSubscriberUpdate(member), subscriberSearch: ``});
  }

  onEditRecipients() {
    this.setState({currentPage: ExportDashboardModalPage.Main});
  }

  /**
   * Same as onExportBoard but it skips the confirmation step.
   * @returns
   */
  onConfirmExternalUsersExport() {
    if (!this.isValidExport) {
      return;
    }

    this.exportBoardApiCall();
  }

  /**
   * Checks if there are any external users in the list of subscribers. If so it
   * prompts the user to confirm that they want to export including external users.
   * If there's no confirmation needed or they confirm, it calls the API to export.
   * @returns
   */
  onExportBoard() {
    if (!this.isValidExport) {
      return;
    }
    if (this.getExternalUsers().length) {
      this.setState({
        currentPage: ExportDashboardModalPage.ConfirmExternalUsers,
      });
      return;
    }
    this.exportBoardApiCall();
  }

  onSlackSubscriptionsChanged(detail: SelectedChannelsChangedDetail) {
    const selectedChannels = detail.selectedChannels;
    const slackTeamId: string = detail.teamId;
    this.setState({
      slackSubscriptions: selectedChannels.map((channel) => {
        return {slack_channel_name: channel.name, slack_channel_id: channel.id, slack_team_id: slackTeamId};
      }),
    });
  }

  onSubscriberSearchKeydown(ev: KeyboardEvent) {
    const emailSearch = this.state.subscriberSearch;
    if (
      ev.code === KEY_VALUES.ENTER &&
      emailSearch.length &&
      !this.updateSubscriberSearchError.length &&
      isValidEmailAddress(emailSearch)
    ) {
      this.setState({
        ...this.addSubscriberUpdate({email: emailSearch}),
        subscriberSearch: ``,
      });
    }
  }

  removeSubscriber(index: number) {
    const emailSubscribers = this.state.emailSubscribers;
    const newSubscribers = [...emailSubscribers];
    newSubscribers.splice(index, 1);
    this.setState({emailSubscribers: newSubscribers});
  }

  updateSubscriberSearch(value: string) {
    this.setState({subscriberSearch: value});
  }

  render() {
    return template.apply(this);
  }
}

export const ConnectedExportDashboardModal = (props: ExportDashboardModalProps) => {
  const currentProject = useCurrentProject();
  const i18n = useTranslation();

  return <ExportDashboardModal {...props} currentProject={currentProject} i18n={i18n} />;
};

export const exportDashboardModalPromise = (props: ExportDashboardModalProps) => {
  return promisifyModal({
    attrs: {},
    closeEvents: [],
    onElementCreatedCallback: (element, onCloseCallback) => {
      const reactRoot = createRoot(element);
      reactRoot.render(<ExportDashboardModal {...props} onClose={() => onCloseCallback()} />);
    },
    props,
    tagName: REACT_MODAL_TAG_NAME,
  });
};
